import React, { useEffect, useState, useRef } from "react";
import { post } from "../utils/api";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../customComponents/Button";
import Table from "../customComponents/Table";
import ConfirmationModal from "../customComponents/ConfirmationModal";
import InputText from "../customComponents/InputText";
import { ToastContainer, toast } from "react-toastify";
import GeneratePackagePDF from "./PackagePdf";
import DatePicker from "../customComponents/DatePicker";
import log from "../assets/img/Itinerary/log.png";
import SearchSelect from "../customComponents/SerachSelect";
import FilterSelect from "../customComponents/FilterSelect";
import LoaderComponent from "./LoaderComponent";

const AllPackages = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchText = searchParams.get("search") || "";
  const packageTypeFilter = searchParams.get("packagetype") || "";
  const cityFilter = searchParams.get("city") || "";
  const destinationFilter = searchParams.get("destination") || "";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const [localSearch, setLocalSearch] = useState(searchText);
  const searchTimerRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [packages, setPackages] = useState([]);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState("");
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [packageOptions, setPackageOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [destOptions, setDestinationOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noOfPages, setNoOfPages] = useState(1);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [selectedPackageForDuplicate, setSelectedPackageForDuplicate] = useState(null);
  const [newPackageName, setNewPackageName] = useState("");

  useEffect(() => {
    fetchPackages();
  }, [currentPage, searchText, packageTypeFilter, cityFilter, destinationFilter]);

  const fetchPackages = async () => {
    setIsLoading(true);
    try {
      const response = await post("/getAllPackages", {
        page: currentPage,
        limit: 10,
        isitenary: false,
        search: searchText.trim(),
        packagetype: packageTypeFilter,
        city: cityFilter,
        destination: destinationFilter,
      });
      if (response.status && response.data) {
        const formattedData = response.data.map((pkg) => ({
          packageName: pkg.packagename,
          packageType: pkg.packagetype,
          source: pkg.city,
          destination: pkg.destination || "-",
          pricePerHead: `₹ ${pkg.price_perhead}`,
          createdonFormatted: new Date(pkg.createdon).toLocaleDateString("en-GB").replace(/\//g, "-"),
          ...pkg,
        }));
        setNoOfPages(response?.total);

        if (!optionsLoaded) {
          const packageSet = new Set(response.data.map((item) => item.packagetype));
          const citySet = new Set(response.data.map((item) => item.city));
          const destinationSet = new Set(response.data.map((item) => item.destination));

          setPackageOptions([
            { label: "All Package", value: "All Package" },
            ...Array.from(packageSet).filter(Boolean).map((p) => ({ label: p, value: p })),
          ]);
          setCityOptions([
            { label: "All City", value: "All City" },
            ...Array.from(citySet).filter(Boolean).map((c) => ({ label: c, value: c })),
          ]);
          setDestinationOptions([
            { label: "All Destination", value: "All Destination" },
            ...Array.from(destinationSet).filter(Boolean).map((d) => ({ label: d, value: d })),
          ]);
          setOptionsLoaded(true);
        }

        setPackages(formattedData);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching packages:", error);
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      updateParams({ search: value || null, page: null });
    }, 400);
  };

  const handleFormSelection = (e, type) => {
    if (type === "city") {
      updateParams({ city: e.value !== "All City" ? e.value : null, page: null });
    } else if (type === "packagetype") {
      updateParams({ packagetype: e.value !== "All Package" ? e.value : null, page: null });
    } else {
      updateParams({ destination: e.value !== "All Destination" ? e.value : null, page: null });
    }
  };

  const handleDeleteClick = (data) => {
    setDeleteModal(true);
    setDeleteItem(data);
  };

  const handleCloseModal = () => {
    setDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await post("/deletePackage", deleteItem);
      toast.success("Package deleted successfully");
      setDeleteModal(false);
      fetchPackages();
    } catch (error) {
      toast.error("Package delete failed");
      setDeleteModal(false);
    }
  };

  const handleEditClick = (packageData) => {
    navigate("/add-packages", {
      state: { packageData, isEdit: true, page: currentPage },
    });
  };

  const handleDuplicateClick = (pkg) => {
    setSelectedPackageForDuplicate(pkg);
    setNewPackageName(`Copy of ${pkg.packagename}`);
    setDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!newPackageName.trim()) return;
    try {
      const response = await post("/duplicatePackage", {
        packagedetailsid: selectedPackageForDuplicate.packagedetailsid,
        packagename: newPackageName.trim(),
        user,
      });
      if (response.status) {
        toast.success("Package duplicated successfully!");
        setDuplicateModal(false);
        fetchPackages();
      } else {
        toast.error("Failed to duplicate package");
      }
    } catch {
      toast.error("Failed to duplicate package");
    }
  };

  const handleLogClick = (rowData) => {
    navigate(`/package-view-logs/${rowData.packagedetailsid}`);
  };

  const columnHelper = createColumnHelper();

  const PackageTypeIcon = ({ type }) => {
    let icon = "";
    let color = "";
    switch (type) {
      case "Domestic": icon = "train"; color = "#F2A64B"; break;
      case "International": icon = "multiple_airports"; color = "#5DA086"; break;
      case "Day Outing": icon = "local_taxi"; color = "#797979"; break;
      case "Day Visit - Local sightseeing": icon = "local_taxi"; color = "#797979"; break;
      case "Domestic - DMC": icon = "train"; color = "#F2A64B"; break;
      default: icon = "local_offer"; color = "#000000";
    }
    return <span className="material-symbols-outlined" style={{ color }}>{icon}</span>;
  };

  const columns = [
    columnHelper.accessor("slNo", {
      header: "Sl. No",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("packageName", { header: "PACKAGE NAME" }),
    columnHelper.accessor("packageType", {
      header: "PACKAGE TYPE",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <PackageTypeIcon type={row.original.packageType} />
          <span>{row.original.packageType}</span>
        </div>
      ),
    }),
    columnHelper.accessor("source", { header: "SOURCE" }),
    columnHelper.accessor("destination", { header: "DESTINATION" }),
    columnHelper.accessor("pricePerHead", { header: "PRICE PER HEAD" }),
    columnHelper.accessor("actions", {
      header: "ACTIONS",
      cell: (info) => (
        <div className="flex gap-2">
          <button className="p-2 bg-gray-200 rounded-md" onClick={() => handleEditClick(info.row.original)}>
            <span className="material-symbols-outlined">edit</span>
          </button>
          <button className="p-2 bg-gray-200 rounded-md" onClick={() => handleDuplicateClick(info.row.original)} title="Duplicate">
            <span className="material-symbols-outlined">content_copy</span>
          </button>
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button onClick={() => handleDeleteClick(info.row.original)} className="p-2 bg-gray-200 text-red-500 rounded-md">
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
          <button className="p-2 bg-gray-200 rounded-md" onClick={() => GeneratePackagePDF(info.row.original)}>
            <span className="material-symbols-outlined">picture_as_pdf</span>
          </button>
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button className="p-2 bg-gray-200 rounded-md" onClick={() => handleLogClick(info.row.original)}>
              <img src={log} alt="Package Logo" className="lg:max-w-full lg:h-auto lg:w-4 w-6 max-w-28" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <h2 className="text-primary-black text-[28px] font-semibold">Packages</h2>
          <p className="text-[14px]">
            <span className="font-bold">Packages</span> /{" "}
            <span className="font-regular">Package List</span>
          </p>
        </div>
        <div className="flex mt-4 justify-end gap-4">
          {user?.role === "Admin" && (
            <Button type="primary" label="Add Package" onClick={() => navigate("/add-packages")} />
          )}
        </div>
      </div>

      {/* Filter Fields */}
      <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2 lg:flex lg:justify-between">
        <div className="w-full md:w-auto lg:w-5/12">
          <input
            type="text"
            className="outline-none p-2 rounded-l-lg w-full h-[40px]"
            placeholder="Search..."
            value={localSearch}
            onChange={handleSearch}
          />
        </div>

        <div className="w-full md:w-auto lg:w-52">
          <FilterSelect
            placeholder="Package Type"
            options={packageOptions}
            value={packageOptions.find((o) => o.value === packageTypeFilter) || null}
            onChange={(e) => handleFormSelection(e, "packagetype")}
          />
        </div>

        <div className="w-full md:w-auto lg:w-52">
          <SearchSelect
            value={cityOptions.find((o) => o.value === cityFilter) || null}
            options={cityOptions}
            onChange={(e) => handleFormSelection(e, "city")}
            placeholder="Select Source..."
          />
        </div>

        <div className="w-full md:w-auto lg:w-52">
          <SearchSelect
            value={destOptions.find((o) => o.value === destinationFilter) || null}
            options={destOptions}
            onChange={(e) => handleFormSelection(e, "destination")}
            placeholder="Select Destination..."
          />
        </div>

        <div className="w-full md:w-auto lg:w-52">
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={(date) => setSelectedDate(date)}
            placeholderText="Select Date..."
            className="w-full mt-0"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border-gray-300 mt-7 pb-6">
        {isLoading ? (
          <LoaderComponent />
        ) : (
          <Table
            columns={columns}
            data={packages}
            noOfPages={noOfPages}
            currentPage={currentPage}
            setCurrentPage={(page) => updateParams({ page: page > 1 ? String(page) : null })}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={isDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Package"
        message={`Are you sure you want to delete the Package "${deleteItem?.packagename}"?`}
      />

      {duplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-2">Duplicate Package</h2>
            <p className="text-sm text-gray-500 mb-4">
              Enter a name for the duplicate of &ldquo;{selectedPackageForDuplicate?.packagename}&rdquo;
            </p>
            <InputText
              placeholder="Enter package name..."
              value={newPackageName}
              onChange={(e) => setNewPackageName(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-6">
              <Button label="Cancel" onClick={() => setDuplicateModal(false)} />
              <Button
                label="Duplicate"
                onClick={handleConfirmDuplicate}
                disabled={!newPackageName.trim()}
              />
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default AllPackages;
